import { GraphQL } from "@rpi/openapi-api";

const ORGANIZATION_EDITOR_ROLES = [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.SuperAdmin];

/** Organization size options — shared with onboarding so the values it writes pre-select here. */
export const ORGANIZATION_SIZE_OPTIONS = [
  "Startup (1-10)",
  "Small Company (11-50)",
  "Mid-Sized Company (51-200)",
  "Large Company (201+)",
];

/** Who may view and edit organization information. */
export const canEditOrganization = (role?: GraphQL.DashboardUserRole): boolean =>
  role !== undefined && ORGANIZATION_EDITOR_ROLES.includes(role);

/** Whether each organization field is editable for the current role and value. */
export interface OrganizationFieldEditability {
  website: boolean;
  size: boolean;
  country: boolean;
}

/** The stored organization values that determine which fields are still editable. */
export interface OrganizationFieldValues {
  website?: string | null;
  size?: string | null;
  country?: string | null;
}

const ORGANIZATION_FIELDS: (keyof OrganizationFieldEditability)[] = ["website", "size", "country"];

/**
 * Builds the mutation payload from the editable fields, trimmed. A field emptied after being
 * provided is sent as "" to clear it (super admin); a field that was never provided is omitted,
 * so the backend distinguishes an untouched field from an explicit clear.
 */
export const buildOrganizationUpdateInput = (
  values: GraphQL.EditOrganizationInformationInput,
  editable: OrganizationFieldEditability,
  initial: OrganizationFieldValues
): GraphQL.EditOrganizationInformationInput => {
  const input: GraphQL.EditOrganizationInformationInput = {};
  for (const field of ORGANIZATION_FIELDS) {
    if (!editable[field]) continue;
    const value = values[field]?.trim();
    if (value) {
      input[field] = value;
    } else if (initial[field]) {
      input[field] = "";
    }
  }
  return input;
};

/**
 * Mirrors the backend provide-once rule: a field may be filled while empty,
 * but once it has a value it is locked — only a super admin may overwrite it.
 */
export const isOrganizationFieldEditable = (
  currentValue: string | null | undefined,
  role?: GraphQL.DashboardUserRole
): boolean => role === GraphQL.DashboardUserRole.SuperAdmin || !currentValue;

/** Whether the role may edit at least one field; when false there is nothing to edit, so the entry point is hidden. */
export const canEditAnyOrganizationField = (
  organization: OrganizationFieldValues | null | undefined,
  role?: GraphQL.DashboardUserRole
): boolean =>
  canEditOrganization(role) &&
  ORGANIZATION_FIELDS.some((field) => isOrganizationFieldEditable(organization?.[field], role));

const HTTP_SCHEMES = ["http:", "https:"];

const tryParseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

/**
 * Whether a parsed URL is a clean http(s) URL whose authority the parser didn't rewrite. The
 * WHATWG parser silently collapses extra authority slashes (https:////evil.com → host evil.com),
 * so requiring the source to begin with `scheme://host` rejects those host-confusion forms.
 */
const isCleanHttpUrl = (url: URL, source: string): boolean =>
  HTTP_SCHEMES.includes(url.protocol) &&
  !!url.hostname &&
  source.toLowerCase().startsWith(`${url.protocol}//${url.host}`);

/**
 * Normalizes a website to an absolute http(s) URL, prepending https:// when no scheme is given
 * (acme.com → https://acme.com) and keeping an entered http:// as-is. Returns undefined when the
 * value carries a non-http(s) scheme (javascript:, mailto:, …), is host-confusing (//evil.com,
 * https:////evil.com), or isn't a structurally valid URL.
 */
export const normalizeWebsiteUrl = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = tryParseUrl(trimmed);
  if (parsed) return isCleanHttpUrl(parsed, trimmed) ? trimmed : undefined;

  const prefixed = `https://${trimmed}`;
  const prefixedParsed = tryParseUrl(prefixed);
  return prefixedParsed && isCleanHttpUrl(prefixedParsed, prefixed) ? prefixed : undefined;
};

/** Whether the website is empty (optional) or a valid http(s) URL once normalized. */
export const isValidWebsiteUrl = (value: string): boolean => !value.trim() || normalizeWebsiteUrl(value) !== undefined;
